import React, { useRef, useState, useEffect } from "react";
import { Button, ButtonGroup, Card, Col, Form, Row } from "react-bootstrap";
import { useGetDownlineCollaborator2Query } from "../../app/features/apiSlice";
import SetPageTitle from "../../components/SetPageTitle";
import { OrgChartComponent } from "../../components/OrgChart";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectUserData } from "../../app/features/DataSlice";
import Select from "react-select";

const colourStyles = {
  option: (styles) => {
    return {
      ...styles,
      color: "#000",
    };
  },
};

const StructureNew = () => {
  const { t } = useTranslation();
  const loggedUser = useSelector(selectUserData);
  const targetRef = useRef(null);
  const sessionId = useRef(Date.now()).current;
  const [orgChart, setOrgChart] = useState([]);
  const [flatUsers, setFlatUsers] = useState([]);
  const [userOptions, setUserOptions] = useState();
  const [uniqueLvls, setUniqueLvls] = useState([]);
  const [lvlOptions, setLvlOptions] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [highlightedId, setHighlightedId] = useState(null);
  const { data, isSuccess } = useGetDownlineCollaborator2Query({ sessionId });

  useEffect(() => {
    if (isSuccess) {
      const userTree = data?.data;
      const loggedUser = data.loggedUser;
      const removeLoggedUserParent = userTree.filter(
        (user) => user.parentId && user.id !== loggedUser.user_id
      );
      const sortedData = removeLoggedUserParent.sort(
        (a, b) => a.level - b.level
      );
      const userOptions = sortedData.map((user) => ({
        value: user.name,
        label: user.name,
      }));
      setUserOptions(userOptions);
      setFlatUsers(sortedData);
      setFilteredUsers(sortedData);
      // Set levels
      const levels = userTree.map((item) => item.level);
      const uniqueLevels = [...new Set(levels)];
      const filteredLevels = uniqueLevels.filter((level) => level !== 0);
      setUniqueLvls(filteredLevels);
      const lvlOption = filteredLevels.map((lvl) => ({
        value: lvl,
        label: lvl,
      }));
      setLvlOptions(lvlOption);
      let updatedData = userTree.map((item) =>
        Object.assign({}, item, { _pagingStep: userTree.length })
      );
      setOrgChart(updatedData);
    }
  }, [isSuccess, data, loggedUser]);

  const scrollToDiv = () => {
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const name = e.target.name.value;
    const level = e.target.level.value;
    const result = flatUsers.filter(
      (user) =>
        (!name || user.name === name) &&
        (!level || user.level === parseInt(level, 10))
    );
    setFilteredUsers(result);
  };

  return (
    <div className="my-3">
      <Card bg="dark1 text-light mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <SetPageTitle title="sidebar.structure" />
        </Card.Header>
        <Card.Body>
          <Row className="mb-3 align-items-center">

            {/* Total Downlines + Total Levels */}
            <Col xs="12" lg="6" className="mb-3 mb-lg-0">
              <div className="d-flex justify-content-center justify-content-lg-start text-center">
                <p className="mb-0">
                  {t("totalDownlines")}
                  <br />
                  {flatUsers.length || 0}
                </p>

                <p className="ms-4 mb-0">
                  {t("totalLevels")}
                  <br />
                  {uniqueLvls.length || 0}
                </p>
              </div>
            </Col>

            {/* Filters */}
            <Col xs="12" lg="6">
              <Form
                onSubmit={handleSubmit}
                onReset={() => setFilteredUsers(flatUsers)}
                className="d-flex flex-column flex-lg-row"
              >
                {/* Name */}
                <Form.Group className="w-100 mb-2 mb-lg-0">
                  <Form.Label>{t("name")}</Form.Label>
                  <Select
                    options={userOptions}
                    styles={colourStyles}
                    name="name"
                  />
                </Form.Group>

                {/* Level */}
                <Form.Group className="w-100 ms-lg-1 mb-2 mb-lg-0">
                  <Form.Label>{t("level")}</Form.Label>
                  <Select
                    options={lvlOptions}
                    styles={colourStyles}
                    name="level"
                  />
                </Form.Group>

                {/* Buttons */}
                <div className="d-flex align-items-end mt-1 mt-lg-0 ms-lg-1">
                  <Button
                    variant="outline-light"
                    type="submit"
                    className="me-1"
                  >
                    {t("view")}
                  </Button>

                  <Button
                    type="reset"
                    variant="outline-light"
                  >
                    {t("reset")}
                  </Button>
                </div>
              </Form>
            </Col>

          </Row>

          <DataTable
            value={filteredUsers}
            sortMode="multiple"
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 25]}
            // ref={dt}
            size={"small"}
            showGridlines
            stripedRows
            removableSort
          // globalFilter={globalFilter}
          >
            <Column sortable field="name" header={t("members")} />
            <Column sortable field="referral_code" header={t("referralCode")} />
            <Column sortable field="affiliateCode" header={t("sponsor")} />
            <Column sortable field="level" header={t("level")} />
            <Column
              body={(rowData) => (
                <ButtonGroup size="sm">
                  <Button
                    variant="outline-light"
                    onClick={() => {
                      setHighlightedId(rowData.id);
                      scrollToDiv();
                    }}
                  >
                    <i className="fa fa-diagram-project"></i>
                  </Button>
                </ButtonGroup>
              )}
            ></Column>
          </DataTable>
        </Card.Body>
      </Card>
      <Card bg="dark1 text-light" ref={targetRef}>
        <Card.Body>
          {orgChart.length > 0 && (
            <OrgChartComponent data={orgChart} highlightedId={highlightedId} />
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default StructureNew;
